import React from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useCrmPrepaidSession, isSessionLocked } from "./store/crmPrepaidSessionStore";

// Presentational only — the session (link ref, phase, polling) lives in
// crmPrepaidSessionStore so it survives the cart Offcanvas being closed.
const formatINR = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

function CrmPrepaidLinkModal() {
  const minimized = useCrmPrepaidSession((s) => s.minimized);
  const phase = useCrmPrepaidSession((s) => s.phase);
  const channels = useCrmPrepaidSession((s) => s.channels);
  const orderDisplayId = useCrmPrepaidSession((s) => s.orderDisplayId);
  const orderStatus = useCrmPrepaidSession((s) => s.orderStatus);
  const lastCheckedAt = useCrmPrepaidSession((s) => s.lastCheckedAt);
  const payload = useCrmPrepaidSession((s) => s.payload);
  const customerDetails = useCrmPrepaidSession((s) => s.customerDetails);
  const sendLink = useCrmPrepaidSession((s) => s.sendLink);
  const minimize = useCrmPrepaidSession((s) => s.minimize);
  const closeSession = useCrmPrepaidSession((s) => s.closeSession);
  const checkStatusOnce = useCrmPrepaidSession((s) => s.checkStatusOnce);

  // While the link is being sent / payment is being awaited, the modal must NOT be closable
  // (no backdrop click, no ESC, no programmatic close) — the agent has to wait for the status.
  const blockClose = isSessionLocked(phase);

  const handleClose = () => {
    if (blockClose) return;
    closeSession();
  };

  // Minimising is only offered once the link is actually out — there is nothing to come
  // back to before that.
  const canMinimize = phase === "awaiting" || phase === "stalled";

  const itemsCount = (payload?.lineItems || []).reduce(
    (sum, i) => sum + Number(i.quantity || 0),
    0
  );

  // Shipping charge shown for the agent's information only (₹50 below the free-shipping
  // threshold, mirrors order-service getShippingCharges). Display only — it is NOT sent to
  // the backend and NOT added to the Payable total shown here (order-service handles the charge).
  const cartTotal = Number(payload?.payableAmount) || 0;
  const shippingCharges = cartTotal > 0 && cartTotal < 600 ? 50 : 0;

  return (
    <Modal show={!minimized} onHide={handleClose} centered contentClassName="crm_prepaid-modal" backdrop={blockClose ? "static" : true} keyboard={!blockClose}>
      <style>{`
        .crm_prepaid-modal { border: none; border-radius: 20px; overflow: hidden; box-shadow: 0 24px 60px rgba(15,23,42,.28); }
        .ps-hero { padding: 30px 24px 26px; text-align: center; position: relative; }
        .ps-hero-pending { background: linear-gradient(135deg,#0ea5e9 0%,#6366f1 100%); }
        .ps-hero-paid { background: linear-gradient(135deg,#059669 0%,#10b981 100%); }
        .ps-hero-expired { background: linear-gradient(135deg,#64748b 0%,#94a3b8 100%); }
        .ps-hero-cancelled { background: linear-gradient(135deg,#dc2626 0%,#f87171 100%); }
        .ps-hero-error { background: linear-gradient(135deg,#b45309 0%,#f59e0b 100%); }
        .ps-badge { width: 82px; height: 82px; margin: 0 auto 14px; border-radius: 50%;
          background: rgba(255,255,255,.16); display: flex; align-items: center; justify-content: center;
          position: relative; }
        .ps-badge svg { width: 40px; height: 40px; color: #fff; }
        .ps-badge.pulsing::before, .ps-badge.pulsing::after { content: ""; position: absolute; inset: 0;
          border-radius: 50%; border: 2px solid rgba(255,255,255,.55); animation: psPulse 1.9s ease-out infinite; }
        .ps-badge.pulsing::after { animation-delay: .95s; }
        @keyframes psPulse { 0% { transform: scale(1); opacity: .8; } 100% { transform: scale(1.7); opacity: 0; } }
        .ps-hero-title { color: #fff; font-size: 20px; font-weight: 800; letter-spacing: -.2px; margin: 0; }
        .ps-hero-sub { color: rgba(255,255,255,.9); font-size: 13px; margin-top: 4px; }
        .ps-order-pill { display: inline-block; margin-top: 12px; background: rgba(255,255,255,.2);
          color: #fff; font-weight: 700; font-size: 15px; padding: 7px 16px; border-radius: 999px; }
        .ps-minimize { position: absolute; top: 14px; right: 16px; width: 32px; height: 32px;
          border: none; border-radius: 10px; background: rgba(255,255,255,.2); color: #fff;
          font-size: 18px; font-weight: 800; line-height: 1; display: flex; align-items: center;
          justify-content: center; }
        .ps-minimize:hover { background: rgba(255,255,255,.32); }
        .ps-body { padding: 20px 24px 24px; background: #fff; }
        .ps-summary { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 14px; padding: 14px 16px; }
        .ps-row { display: flex; justify-content: space-between; align-items: center; font-size: 13.5px; }
        .ps-row + .ps-row { margin-top: 10px; }
        .ps-label { color: #64748b; }
        .ps-value { color: #0f172a; font-weight: 700; }
        .ps-amount { font-size: 18px; font-weight: 800; color: #0f172a; }
        .ps-chan { display: flex; gap: 8px; justify-content: center; margin-top: 16px; }
        .ps-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700;
          padding: 5px 12px; border-radius: 999px; }
        .ps-chip-on { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
        .ps-chip-off { background: #f1f5f9; color: #94a3b8; border: 1px solid #e2e8f0; }
        .ps-send-btn { width: 100%; margin-top: 18px; border: none; border-radius: 12px; padding: 13px;
          font-weight: 800; font-size: 15px; background: linear-gradient(135deg,#10b981,#059669); }
        .ps-send-btn:disabled { opacity: .7; }
        .ps-await { text-align: center; margin-top: 16px; color: #475569; font-size: 13.5px; font-weight: 600;
          display: flex; align-items: center; justify-content: center; gap: 8px; }
      `}</style>

      <div
        className={`ps-hero ${
          phase === "paid"
            ? "ps-hero-paid"
            : phase === "expired"
            ? "ps-hero-expired"
            : phase === "cancelled"
            ? "ps-hero-cancelled"
            : phase === "error" || phase === "refunded"
            ? "ps-hero-error"
            : "ps-hero-pending"
        }`}
      >
        {canMinimize && (
          <button
            type="button"
            className="ps-minimize"
            onClick={minimize}
            title="Minimise — keep working on this page while the customer pays"
            aria-label="Minimise"
          >
            –
          </button>
        )}
        <div className={`ps-badge ${phase === "awaiting" ? "pulsing" : ""}`}>
          {phase === "paid" ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : phase === "cancelled" || phase === "error" || phase === "refunded" ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <p className="ps-hero-title">
          {phase === "paid"
            ? "Payment Received"
            : phase === "expired"
            ? "Link Expired"
            : phase === "cancelled"
            ? "Link Cancelled"
            : phase === "refunded"
            ? "Payment Refunded"
            : phase === "error"
            ? "Something went wrong"
            : phase === "stalled"
            ? "Still Waiting"
            : "Awaiting Payment"}
        </p>
        <p className="ps-hero-sub">
          {phase === "paid"
            ? "The order has been created successfully."
            : phase === "awaiting"
            ? "Link delivered — waiting for the customer to pay."
            : phase === "expired"
            ? "This payment link is no longer valid."
            : phase === "cancelled"
            ? "This payment link was cancelled."
            : phase === "refunded"
            ? "The paid amount didn't match — it was auto-refunded and no order was created."
            : phase === "error"
            ? "The link could not be sent. Please try again."
            : phase === "stalled"
            ? "We stopped auto-checking after 10 minutes. You can check again any time."
            : "Send the payment link to the customer."}
        </p>
        {phase === "paid" && orderDisplayId && (
          <span className="ps-order-pill">
            Order {orderDisplayId}
            {orderStatus ? ` · ${orderStatus}` : ""}
          </span>
        )}
      </div>

      <div className="ps-body">
        <div className="ps-summary">
          <div className="ps-row">
            <span className="ps-label">Customer</span>
            <span className="ps-value">
              {customerDetails?.first_name || "Customer"}
              {customerDetails?.phone_number ? ` · ${customerDetails.phone_number}` : ""}
            </span>
          </div>
          <div className="ps-row">
            <span className="ps-label">Items</span>
            <span className="ps-value">{itemsCount}</span>
          </div>
          {shippingCharges > 0 && (
            <div className="ps-row">
              <span className="ps-label">Shipping charges</span>
              <span className="ps-value">{formatINR(shippingCharges)}</span>
            </div>
          )}
          <div className="ps-row">
            <span className="ps-label">Payable</span>
            <span className="ps-amount">{formatINR(payload?.payableAmount)}</span>
          </div>
          {shippingCharges > 0 && (
            <div className="ps-row">
              <span className="ps-label">Total amount to be paid</span>
              <span className="ps-amount">{formatINR((Number(payload?.payableAmount) || 0) + shippingCharges)}</span>
            </div>
          )}
        </div>

        {(phase === "awaiting" || phase === "stalled" || phase === "paid") && (
          <div className="ps-chan">
            <span className={`ps-chip ${channels.waba ? "ps-chip-on" : "ps-chip-off"}`}>
              {channels.waba ? "✓" : "–"} WhatsApp
            </span>
            <span className={`ps-chip ${channels.sms ? "ps-chip-on" : "ps-chip-off"}`}>
              {channels.sms ? "✓" : "–"} SMS
            </span>
          </div>
        )}

        {(phase === "idle" || phase === "sending" || phase === "error") && (
          <Button
            className="ps-send-btn"
            disabled={phase === "sending"}
            onClick={sendLink}
          >
            {phase === "sending" && <Spinner size="sm" className="me-2" />}
            {phase === "error" ? "Retry — Send via WhatsApp & SMS" : "Send link via WhatsApp & SMS"}
          </Button>
        )}

        {phase === "awaiting" && (
          <div className="ps-await">
            <Spinner size="sm" /> Waiting for payment…
          </div>
        )}

        {phase === "stalled" && (
          <>
            {lastCheckedAt && (
              <div className="ps-await">
                Last checked{" "}
                {new Date(lastCheckedAt).toLocaleTimeString("en-IN", {
                  hour: "numeric",
                  minute: "2-digit",
                })}{" "}
                · still pending
              </div>
            )}
            <div className="d-flex gap-2 mt-3">
              <Button variant="primary" className="w-50" onClick={checkStatusOnce}>
                Check status
              </Button>
              <Button variant="outline-secondary" className="w-50" onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        )}

        {(phase === "paid" || phase === "expired" || phase === "cancelled" || phase === "refunded") && (
          <Button variant="outline-secondary" className="w-100 mt-3" onClick={handleClose}>
            Close
          </Button>
        )}
      </div>
    </Modal>
  );
}

export default CrmPrepaidLinkModal;
